import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, House, Package, SearchX } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState as UIEmptyState } from '@/components/ui/empty-state';
import { Typography } from '@/components/ui/typography';
import { Spinner } from '@/components/ui/spinner';
import { MultiSelect, Option } from '@/components/ui/multi-select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LayoutTypes } from './table-action-bar/model/tableActionBar';
import { TableActionBar } from './table-action-bar/TableActionBar';
import MoreFiltersDrawer, { ActiveStatusOption } from './components/MoreFiltersDrawer';
import DateRangeFilter, { DateRangeValue } from './date-range-filter/DateRangeFilter';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { PMSSync } from '../../pms/pms-sync/PMSSync';
import { EditCancelRequestModal } from './components/EditCancelRequestModal';
import { getOrderStatusOptions } from '../../orders/table-cell/orderCellService';
import { isChildEntityScoped, shouldShowEntityFilter } from '../../../services/entity-tree';
import { usePropertyFilters } from './hooks/usePropertyFilters';
import { useOrderView } from './hooks/useOrderView';
import { usePropertyListData } from './hooks/usePropertyListData';
import { CardGrid } from './card-grid/CardGrid';
import { PropertyCard } from './card-grid/PropertyCard';
import { OrderCard } from './card-grid/OrderCard';
import { PropertyCartProvider } from './PropertyCartContext';
import { PropertyDetailDrawer } from './property-detail-drawer/PropertyDetailDrawer';
import { NewOrderSplitButton } from '@/components/orders/new-order-drawer/NewOrderSplitButton';
import { NewOrderDrawer } from '@/components/orders/new-order-drawer/NewOrderDrawer';
import { NewPropertySplitButton } from '@/components/properties/new-property-drawer/NewPropertySplitButton';
import { NewPropertyDrawer } from '@/components/properties/new-property-drawer/NewPropertyDrawer';
import {
  useGetCartQuery,
  useGetOrderFilterMetadataQuery,
} from '@/components/orders/services/orderApi';
import FilterTray, { LockedStatusChip } from './components/FilterTray';
import { buildFilterChips } from './utils/buildFilterChips';
import {
  countOrderFilters,
  countPropertyMoreFilters,
  createOrderParams,
  restoreOrderFiltersFromQS,
} from './utils/advancedFilterUtils';
import { canCreateOnRoute, getPermissionForAction } from '@/util/permissions';
import { BELOW_DESKTOP_QUERY } from '@/config/ui.constants';
import type { PropertyTable } from './metadata/propertyConfig';
import { useSelectedEntityId } from '@/hooks/useEntityScope';

export const PropertyListPage = memo(function PropertyListPage({
  isProperty, canIntegrate = false, canCreate = true,
}: {
  isProperty: boolean;
  // canPut/canGet/canPost/canDelete are plumbed from App.tsx but not read here.
  // They are the raw permission-table answers; `canCreate` is the gate this page
  // actually renders on (see util/permissions.canCreateOnRoute for why the two
  // differ today). Left in place because they are already wired and the
  // migration away from canCreate will consume canPost directly.
  canPut?: boolean; canGet?: boolean; canPost?: boolean; canDelete?: boolean;
  canIntegrate?: boolean;
  /** Defaults to true so existing callers and specs keep today's behaviour. */
  canCreate?: boolean;
}) {
  const isInitializedRef = useRef(false);
  const [drawerProperty, setDrawerProperty] = useState<PropertyTable | null>(null);
  const [isNewOrderDrawerOpen, setIsNewOrderDrawerOpen] = useState(false);
  const [isNewPropertyDrawerOpen, setIsNewPropertyDrawerOpen] = useState(false);
  const onOpenDrawer = (item: PropertyTable) => setDrawerProperty(item);
  const listData = usePropertyListData(
    isProperty,
    isProperty ? onOpenDrawer : undefined,
  );
  const filters = usePropertyFilters(isProperty, listData.onPageChange, listData.page, listData.sort);
  // Hoisted from the three callbacks below, which each read it at call time:
  // one scope source rather than three readers of it (DV-3013 §2.1 class B).
  const selectedEntityId = useSelectedEntityId() ?? '';
  const orderView = useOrderView();
  // Below lg, the inline filter row collapses to search + one "Filters"
  // button; the quick-filter controls (Order Status/Date/Entity or unit
  // status) move into the MoreFiltersDrawer sheet. One tree mounted per
  // viewport — CSS-hiding would duplicate the #search-property id.
  const isBelowLg = useMediaQuery(BELOW_DESKTOP_QUERY);
  // Below lg, properties always render as cards regardless of the persisted
  // cardView preference — the toggle that writes that preference is hidden
  // below lg (TableActionBar), so this only forces the *read* side; desktop
  // keeps the toggle + preference exactly as before. Orders never have a
  // card/list toggle (isCardView is always false there), so this is
  // properties-only by construction.
  const effectiveCardView = isProperty
    ? isBelowLg || listData.isCardView
    : listData.isCardView;
  // A viewport resize across 1024px flips effectiveCardView the same way
  // onActionClick's manual toggle does -- unmounting CardGrid/DataTable and
  // mounting the other -- but onActionClick is the only place that sets
  // skipInitialPaginationRef, so a resize-driven flip was falling through
  // to the newly-mounted DataTablePagination's onPageChange-on-mount firing
  // for real (an unrequested appendFilter + selection.clear()). Children
  // effects fire before parent effects, so a useEffect here would run too
  // late to beat that mount; comparing against the previous render's value
  // during render (not in an effect) is what lets the gate be set before
  // the new pagination mounts, matching onActionClick's synchronous-gate
  // reasoning above.
  const prevEffectiveCardViewRef = useRef<boolean | null>(null);
  if (
    prevEffectiveCardViewRef.current !== null &&
    prevEffectiveCardViewRef.current !== effectiveCardView
  ) {
    listData.skipInitialPaginationRef.current = true;
  }
  prevEffectiveCardViewRef.current = effectiveCardView;

  // Staged copies of the quick-filter state for the mobile sheet. Initialized
  // from the applied values each time the sheet opens (not on every render)
  // so edits inside the sheet don't refetch until Apply is pressed — chips,
  // FilterTray, and the desktop inline row all read the *applied* state
  // above, untouched by these.
  const [stagedOrderStatusValues, setStagedOrderStatusValues] = useState(filters.orderStatusValues);
  const [stagedOrderDateRange, setStagedOrderDateRange] = useState<DateRangeValue | null>(filters.orderDateRange);
  const [stagedSelectedEntityFilters, setStagedSelectedEntityFilters] = useState(filters.selectedEntityFilters);
  const [stagedPropertyStatus, setStagedPropertyStatus] = useState(filters.filter.status ?? '');
  useEffect(() => {
    if (!filters.openMoreFilters) return;
    setStagedOrderStatusValues(filters.orderStatusValues);
    setStagedOrderDateRange(filters.orderDateRange);
    setStagedSelectedEntityFilters(filters.selectedEntityFilters);
    setStagedPropertyStatus(filters.filter.status ?? '');
    // Re-sync only when the sheet transitions open; the applied values below
    // are read from the filters/listData closures captured at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.openMoreFilters]);

  // Commits the staged quick-filter values in the SAME call as the sheet's
  // advanced-filter Apply (filters.onApplyFilters's quickFilterCommit param)
  // -- one setFilters batch, one appendFilter, one refetch, whether the user
  // edited quick fields, advanced fields, or both in this sheet visit. Two
  // separate onApplyFilters-shaped calls here would each read the other's
  // pre-update filter/moreFilters snapshot and could silently drop whichever
  // change fired first.
  const onApplyQuickFilters = (mf: string, activeStatusValue: ActiveStatusOption | null) => {
    if (isProperty) {
      filters.onApplyFilters(mf, activeStatusValue, { propertyStatus: stagedPropertyStatus || null });
      return;
    }
    filters.onApplyFilters(mf, activeStatusValue, {
      orderStatusValues: stagedOrderStatusValues,
      orderDateRange: stagedOrderDateRange,
      selectedEntityFilters: stagedSelectedEntityFilters,
    });
  };
  // Order filter metadata powers the slug→name map so the Services Ordered
  // chip shows the service name rather than its shoppingCartSlug.
  const { data: orderFilterMetadata } = useGetOrderFilterMetadataQuery(undefined, {
    skip: isProperty,
  });
  const serviceNameBySlug = useMemo(
    () =>
      Object.fromEntries(
        (orderFilterMetadata?.services ?? []).map((s) => [
          s.shoppingCartSlug,
          s.name,
        ]),
      ),
    [orderFilterMetadata],
  );

  // Bulk Add-to-Cart (checkboxes + TableActionBar) and the per-card cart button
  // are checkout affordances gated exactly like the cart route (DV-2751). Keying
  // the selection checkboxes on the same signal avoids leaving interactive-but-
  // dead selection UI when the bulk action is withheld.
  const canAddToCart = !!isProperty && canCreateOnRoute('Order');
  // Roles the permission table denies 'GET :Cart' (INTERNAL_POST_PROCESSING)
  // must not fire the cart query — a backend 403 would send baseQueryWithAuth
  // into a single-use-refresh-token rotation + eventual endSession (spurious
  // logout mid-work). Skip the request instead.
  const canViewCart = getPermissionForAction('Cart', 'GET');

  // Cart membership drives the "Added to cart" success state on card +
  // drawer Add-to-cart buttons. RTK Query dedupes this with the same
  // call inside NewOrderDrawer, so there's no extra network cost.
  const userId = localStorage.getItem('userId') ?? '';
  const { data: cart } = useGetCartQuery(userId, {
    skip: !isProperty || !userId || !canViewCart,
  });
  const propertyIdsInCart = useMemo(
    () => new Set((cart?.items ?? []).map((item) => item.propertyId)),
    [cart?.items],
  );

  // Live add-to-cart state for the frozen-right table cells (see
  // PropertyAddToCartCell). The card grid gets the same state via render props.
  const cartContextValue = useMemo(
    () => ({
      addingPropertyId: listData.addingPropertyId,
      propertyIdsInCart,
      onAddToCart: listData.handleAddToCart,
    }),
    [listData.addingPropertyId, propertyIdsInCart, listData.handleAddToCart],
  );

  useEffect(() => {
    if (isInitializedRef.current) return;
    const init = filters.getInitialFilterChain();
    if (!init.entityValue) return;
    // DV-3455: reopen on the page the user left. A deep link is a fresh entry
    // to a targeted order, so it starts on page 1; the page size and sort
    // are preferences and carry. The sort is the hook's restored state, or
    // the view's default when none has been chosen (DV-3122).
    const { page, pageSize } = listData.restoredPaging;
    listData.onPageChange(
      init.isDeepLink ? 1 : page, pageSize,
      init.filterStr, init.sortStr, init.queryParamData, init.entityValue,
    );
    isInitializedRef.current = true;
  }, []);

  const onActionClick = (view: string) => {
    // Toggling unmounts CardGrid/DataTable and remounts the other; the
    // newly-mounted DataTablePagination's onPageChange-on-mount fires
    // BEFORE usePropertyListData's gate-reset useEffect can run in the
    // same commit cycle (children effects fire before parent effects),
    // so the gate may be stale-false from the previous mount's
    // consumption — causing a needless fetch and a flicker. Set the
    // gate synchronously here so it's already true by the time the
    // new pagination mounts.
    listData.skipInitialPaginationRef.current = true;
    listData.setIsCardView(view === LayoutTypes.CARD);
  };
  const handleChangeNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    listData.setChangeNotes(e.target.value);
    if (listData.inputRef.current) listData.inputRef.current.focus();
    if (listData.changeNotes === '') listData.setIsValid(false);
  };
  // The table and card grid mount once rows have landed, and remount on a
  // layout flip; either way they open on the page that was fetched (DV-3455).
  // `page[0]` is 0 only before the first fetch, when neither is mounted.
  const seededPage = Math.max(listData.page[0], 1);
  const syncProperties = () => filters.appendFilter(filters.filter, listData.page[0], listData.page[1], listData.sort, filters.moreFilters);
  const handleSort = (sel: string | undefined, asc: boolean | undefined) =>
    listData.onSort(sel ?? null, asc ?? null, (_, p, s, ss) => filters.appendFilter(filters.filter, p as number, s as number, ss as string));

  const removeOrderStatus = (value: string) => {
    const next = filters.orderStatusValues.filter((o) => o.value !== value);
    filters.setOrderStatusValues(next);
    const ft = { ...filters.filter, orderStatus: next.length > 0 ? next.map((o) => o.value) : null };
    filters.setFilter(ft); filters.checkFilterObject(ft);
    filters.appendFilter(ft, 1, 25, listData.sort, filters.moreFilters);
  };
  const removeOrderDate = () => {
    filters.setOrderDateRange(null);
    const ft = { ...filters.filter, orderDateFrom: null, orderDateTo: null };
    filters.setFilter(ft); filters.checkFilterObject(ft);
    filters.appendFilter(ft, 1, 25, listData.sort, filters.moreFilters);
  };
  const removeEntity = (value: string) => {
    const cId = selectedEntityId;
    const candidate = filters.selectedEntityFilters.filter((o) => o.value !== value);
    const next = isChildEntityScoped() && !candidate.some((o) => o.value === cId)
      ? [filters.entityFilterOptions.find((o) => o.value === cId)!, ...candidate]
      : candidate;
    filters.setSelectedEntityFilters(next);
    const ft = { ...filters.filter, entityUUIDs: next.length > 0 ? next.map((o) => o.value) : null };
    filters.setFilter(ft); filters.checkFilterObject(ft);
    filters.appendFilter(ft, 1, 25, listData.sort, filters.moreFilters);
  };
  const removePropertyStatus = () => filters.handleFilterChange('propertyStatus', '');
  const removeActiveStatus = () => {
    // Reset "Properties Displayed" to its Active Units default.
    const active = { value: 'Active Units', label: 'Active Units' };
    filters.onApplyFilters(filters.moreFilters, active);
    filters.setFiltersCount(countPropertyMoreFilters(filters.moreFilters, active));
  };

  const filterChips = buildFilterChips({
    isProperty: !!isProperty,
    filter: filters.filter,
    orderStatusValues: filters.orderStatusValues,
    orderDateRange: filters.orderDateRange,
    selectedEntityFilters: filters.selectedEntityFilters,
    isLocked: !isProperty && orderView.isLocked,
    moreFilters: filters.moreFilters,
    serviceNameBySlug,
    activeStatus: filters.activeStatus ?? null,
    removeOrderStatus,
    removeOrderDate,
    removeEntity,
    removePropertyStatus,
    removeActiveStatus,
    onRemoveAdvanced: (next: string) => {
      filters.onApplyFilters(next, filters.activeStatus ?? null);
      if (isProperty) {
        filters.setFiltersCount(countPropertyMoreFilters(next, filters.activeStatus));
        return;
      }
      const r = restoreOrderFiltersFromQS(next);
      filters.setFiltersCount(
        countOrderFilters(
          createOrderParams({
            address: r.simple.address,
            unableToComplete: r.unableToComplete ?? undefined,
            accessType: r.simple.accessType,
            appointmentDateRange: r.appointmentDateRange,
            orderCompletedDateRange: r.orderCompletedDateRange,
            services: r.serviceSlugs.map((s) => ({ value: s, label: s })),
            orderedBy: r.orderedByValues,
          })
        )
      );
    },
  });

  const lockedChip: LockedStatusChip | null =
    !isProperty && orderView.isLocked
      ? { label: 'Order Status', viewName: orderView.view.name, statuses: orderView.lockedOptions }
      : null;

  // When the chip tray is showing, tighten the bar's bottom padding so the
  // chips sit close to the table instead of leaving the full no-tray gap.
  const hasFilterTray = !!lockedChip || filterChips.length > 0;

  // Order Status control (orders). On a preset view it's locked: the
  // MultiSelect is disabled, shows the preset's statuses, and is wrapped in a
  // tooltip explaining why it can't be changed (matches the design). Shared
  // by the desktop row (applied binding) and the mobile sheet's quickFilters
  // slot (staged binding) so the locked/unlocked/tooltip structure and the
  // shared `disabled` expression can't drift between the two copies.
  const renderOrderStatusControl = ({
    value,
    onChange,
    lockTestId,
  }: {
    value: { value: string; label: string }[];
    onChange: (selected: Option[]) => void;
    lockTestId: string;
  }) => {
    const control = (
      <MultiSelect
        options={getOrderStatusOptions()}
        value={orderView.isLocked ? orderView.lockedOptions : value}
        placeholder="Order Status"
        displayMode="count"
        searchable={false}
        disabled={listData.isNoList || orderView.isLocked}
        onChange={onChange}
      />
    );
    if (!orderView.isLocked) return control;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div tabIndex={0} data-testid={lockTestId}>
              {control}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Locked by &quot;{orderView.view.name}&quot; view
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const applyOrderStatusChange = (selected: Option[]) => {
    const selStr = selected.map((o) => ({ value: String(o.value), label: o.label }));
    filters.setOrderStatusValues(selStr);
    const ft = { ...filters.filter, orderStatus: selStr.length > 0 ? selStr.map((o) => o.value) : null };
    filters.setFilter(ft); filters.checkFilterObject(ft);
    filters.appendFilter(ft, 1, 25, listData.sort, filters.moreFilters);
  };

  // Same search <Input> element/props used by both the desktop row and the
  // mobile search-plus-Filters pair below — only one of the two ever mounts
  // (isBelowLg), so sharing the id/testid can't collide in the DOM.
  const searchInput = (
    <Input id="search-property" name="search-property" type="search"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => filters.handleFilterChange('propertyAddress', e.target.value)}
      ref={filters.addressRef as React.Ref<HTMLInputElement>} value={filters.filter.address ?? ''}
      disabled={listData.isNoList} placeholder={isProperty ? 'Search by Address, Portfolio, or Property ID' : 'Search by Address, Order ID, or Property ID'}
      data-testid="search-property_id" />
  );

  // Staged quick-filter controls for the mobile sheet — the same
  // MultiSelect/DateRangeFilter/Input controls the desktop row uses, wired to
  // the staged setters above instead of the applied filter state. Committed
  // in one batch by onApplyQuickFilters on the sheet's Apply.
  const quickFilters = isProperty ? (
    <Input id="mobile-property-status" name="propertyStatus" type="search"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStagedPropertyStatus(e.target.value)}
      value={stagedPropertyStatus} disabled={listData.isNoList}
      placeholder="Unit Status" data-testid="mobile-property-status_id" />
  ) : (
    <>
      {renderOrderStatusControl({
        value: stagedOrderStatusValues,
        onChange: (sel) => setStagedOrderStatusValues(sel.map((o) => ({ value: String(o.value), label: o.label }))),
        lockTestId: 'mobile-order-status-lock',
      })}
      <DateRangeFilter label="Order Date" value={stagedOrderDateRange} disabled={listData.isNoList}
        onChange={(range) => setStagedOrderDateRange(range)} />
      {shouldShowEntityFilter() && (
        <MultiSelect
          options={filters.entityFilterOptions}
          value={stagedSelectedEntityFilters}
          placeholder="Entity"
          displayMode="count"
          searchable={false}
          disabled={listData.isNoList}
          onChange={(sel) => {
            const cId = selectedEntityId;
            const selStr = sel.map((o) => ({ value: String(o.value), label: o.label }));
            const final = isChildEntityScoped() && !selStr.some((v) => v.value === cId)
              ? [filters.entityFilterOptions.find((o) => o.value === cId)!, ...selStr] : selStr;
            setStagedSelectedEntityFilters(final);
          }}
        />
      )}
    </>
  );

  return (
    <PropertyCartProvider value={cartContextValue}>
    <div className="flex flex-col bg-background h-full overflow-hidden">
      <section className={`shrink-0 bg-background pt-3 ${listData.isNoListFilter ? 'filer-height' : ''}`}>
        <section className={`px-4 pt-2 ${hasFilterTray ? 'pb-2' : 'pb-6'} ${!isProperty ? 'border-b border-border' : ''}`}>
          <div className="flex items-center max-lg:flex-wrap">
            {/* max-lg: the view name drops under the title instead of sitting
                beside it — side by side, the pair competes with the New Order
                button for a phone's row width and forces the title to wrap. */}
            <div className="flex items-baseline gap-3 p-2 max-lg:flex-col max-lg:items-start max-lg:gap-0">
              <Typography variant="heading-xl">
                {isProperty ? 'Properties' : 'Orders'}
              </Typography>
              {!isProperty && orderView.view.key !== 'all' && (
                <span className="text-base font-normal text-muted-foreground">
                  {orderView.view.name}
                </span>
              )}
            </div>
            {isProperty && canIntegrate && (
              // max-lg: the sync status drops to its own full-width row below
              // the title — inline, it collides with the Add Property button
              // at phone widths (QA finding, 2026-08-17; the e2e fixture stubs
              // PMS connections empty, so the gate never renders this state).
              // max-lg:-mt-2 pulls the row back against the title: the
              // Refresh button's 44px touch floor centers its text, which
              // otherwise reads as a large empty gap under "Properties".
              <div className="min-w-0 max-lg:order-3 max-lg:basis-full max-lg:-mt-2 max-lg:pb-1">
                <PMSSync onSyncStarted={syncProperties}
                  handleFilterChange={filters.handleFilterChange} setIsNoList={listData.setIsNoList} />
              </div>
            )}
            {!isProperty && canCreate && (
              <div className="ml-auto pr-2">
                <NewOrderSplitButton
                  onOpenDrawer={() => setIsNewOrderDrawerOpen(true)}
                />
              </div>
            )}
            {isProperty && canCreate && (
              <div className="ml-auto pr-2">
                <NewPropertySplitButton
                  onOpenDrawer={() => setIsNewPropertyDrawerOpen(true)}
                  canIntegrate={canIntegrate}
                />
              </div>
            )}
          </div>
          {/* Filters apply on change; Enter's implicit submission would be a
              native GET that reloads the page with the search as a query string. */}
          <form onSubmit={(e) => e.preventDefault()}>
            {isBelowLg ? (
              <div className="flex items-center gap-2 mt-4">
                <div className="min-w-0 flex-1">{searchInput}</div>
                <Button variant="outline" type="button" disabled={listData.isNoList}
                  onClick={filters.hideModal} data-testid="mobile-filters-button" className="shrink-0">
                  Filters
                  {filterChips.length > 0 && (
                    <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold leading-none">
                      {filterChips.length}
                    </span>
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3 mt-4">
                <div className="min-w-[360px]">{searchInput}</div>
                {!isProperty && (
                  <div className="w-[190px]">
                    {renderOrderStatusControl({
                      value: filters.orderStatusValues,
                      onChange: applyOrderStatusChange,
                      lockTestId: 'order-status-lock',
                    })}
                  </div>
                )}
                {isProperty && (
                  <div className="w-[190px]">
                    <Input id="property status" name="propertyStatus" type="search"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => filters.handleFilterChange('propertyStatus', e.target.value)}
                      value={filters.filter.status ?? ''} disabled={listData.isNoList}
                      ref={filters.propertyStatusRef as React.Ref<HTMLInputElement>} placeholder="Unit Status" data-testid="property status_id" />
                  </div>
                )}
                {!isProperty && (
                  <div className="w-[190px]">
                    <DateRangeFilter label="Order Date" value={filters.orderDateRange} disabled={listData.isNoList}
                      onChange={(range) => {
                        filters.setOrderDateRange(range);
                        const ft = { ...filters.filter, orderDateFrom: range?.from ?? null, orderDateTo: range?.to ?? null };
                        filters.setFilter(ft); filters.checkFilterObject(ft);
                        filters.appendFilter(ft, 1, 25, listData.sort, filters.moreFilters);
                      }} />
                  </div>
                )}
                {!isProperty && shouldShowEntityFilter() && (
                  <div className="w-[190px]">
                    <MultiSelect
                      options={filters.entityFilterOptions}
                      value={filters.selectedEntityFilters}
                      placeholder="Entity"
                      displayMode="count"
                      searchable={false}
                      disabled={listData.isNoList}
                      onChange={(sel) => {
                        const cId = selectedEntityId;
                        const selStr = sel.map((o) => ({ value: String(o.value), label: o.label }));
                        const final = isChildEntityScoped() && !selStr.some((v) => v.value === cId)
                          ? [filters.entityFilterOptions.find((o) => o.value === cId)!, ...selStr] : selStr;
                        filters.setSelectedEntityFilters(final);
                        const ft = { ...filters.filter, entityUUIDs: final.length > 0 ? final.map((o) => o.value) : null };
                        filters.setFilter(ft); filters.checkFilterObject(ft);
                        filters.appendFilter(ft, 1, 25, listData.sort, filters.moreFilters);
                      }}
                    />
                  </div>
                )}
                <div>
                  <Button variant="outline" type="button" disabled={listData.isNoList} onClick={filters.hideModal} data-testid="More filters_id"
                    className="w-[190px] px-3 text-muted-foreground text-sm font-normal h-10 flex items-center">
                    <span className="flex flex-1">More Filters</span>
                    {filters.filtersCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold leading-none ml-1.5 shrink-0">{filters.filtersCount}</span>
                    )}
                    <ChevronDown className="h-4 w-4 ml-0" />
                  </Button>
                </div>
                {/* The old "Clear Filters" link is removed — the filter tray's
                    "Clear all" now serves this role on both lists. */}
              </div>
            )}
            <MoreFiltersDrawer
              open={filters.openMoreFilters}
              onOpenChange={(next) => { if (next !== filters.openMoreFilters) filters.hideModal(); }}
              isProperty={!!isProperty}
              filter={filters.moreFilters}
              activeStatus={filters.activeStatus}
              onApplyFilters={filters.onApplyFilters}
              onResetFilters={filters.onResetFilters}
              setFiltersCount={filters.setFiltersCount}
              quickFilters={quickFilters}
              // Wired unconditionally, NOT gated on isBelowLg: gating made
              // the prop vanish while the panel was open (a tablet rotating
              // across 1024px), so Apply fell through to the 2-argument
              // onApplyFilters and silently dropped any quick-filter edits
              // staged below lg. Routing desktop Apply through the staged
              // commit is safe by construction: staged state re-syncs from
              // applied state every time the panel opens, and both the Sheet
              // and the Drawer are modal, so applied quick state cannot
              // change underneath an open panel — on desktop the merged
              // quickFilterCommit is always a no-op re-assert of the applied
              // values.
              onApplyQuickFilters={onApplyQuickFilters}
              // Below lg the sheet's Reset must clear the quick filters the
              // sheet itself hosts, not just the advanced set — one commit,
              // one refetch, same semantics as the FilterTray's "Clear all"
              // (the drawer only routes Reset here below lg; desktop Reset
              // stays advanced-scoped via onResetFilters).
              onResetAllFilters={() => filters.clearFilter(listData.sort)}
            />
          </form>
          <FilterTray
            chips={filterChips}
            lockedChip={lockedChip}
            onClearAll={() => filters.clearFilter(listData.sort)}
          />
        </section>
      </section>
      <section className={`relative flex-1 min-h-0 overflow-hidden bg-background ${effectiveCardView ? 'card-view' : 'list-view'} ${listData.isLoading ? 'table-loading' : ''} ${!isProperty ? 'order-list' : ''}`}>
        {listData.isLoading && (
          // z-30 to outrank DataTable sticky cells (body z-[10], header
          // corners z-[20]) so the loader fully covers the table, not
          // just the non-sticky columns.
          <div className="absolute flex items-center justify-center inset-0 z-30 bg-background"><Spinner /></div>
        )}
        {listData.tableData.length > 0 && effectiveCardView && (
          <CardGrid
            items={listData.tableData}
            selection={listData.selection}
            isLoading={false}
            showCheckboxes={canAddToCart}
            totalRowsTitle={isProperty ? 'Properties' : 'Orders'}
            totalAvailableRows={listData.totalAvailableRows}
            actionBar={<TableActionBar onActionClick={onActionClick} isProperty={isProperty}
              isCardView={listData.isCardView} onTableSort={handleSort}
              initialSort={listData.activeSort} selection={listData.selection} />}
            paginationProps={{
              totalRows: listData.totalAvailableRows,
              initialPage: seededPage,
              initialRowsPerPage: listData.page[1],
              onPageChange: (_u, _l, p, s) => {
                if (listData.skipInitialPaginationRef.current) { listData.skipInitialPaginationRef.current = false; return; }
                listData.selection.clear();
                filters.appendFilter(filters.filter, p, s);
              },
            }}
            renderCard={(item) => (
              <PropertyCard
                item={item}
                isSelected={listData.selection.isSelected(item.id)}
                onSelectChange={(c) => listData.selection.toggleRow(item.id, c)}
                canAddToCart={canAddToCart}
                onAddToCart={listData.handleAddToCart}
                onOpen={isProperty ? onOpenDrawer : undefined}
                isAddingToCart={listData.addingPropertyId === item.id}
                isInCart={propertyIdsInCart.has(item.id)}
              />
            )}
          />
        )}
        {listData.tableData.length > 0 && !effectiveCardView && (
          <DataTable
            columns={listData.getColumns()}
            tableData={listData.tableData}
            filters={[]}
            totalRowsTitle={isProperty ? 'Properties' : 'Orders'}
            selection={listData.selection}
            isLoading={false}
            isServerSide
            onServerPageChange={(p: number, s: number) => {
              if (listData.skipInitialPaginationRef.current) { listData.skipInitialPaginationRef.current = false; return; }
              listData.selection.clear();
              filters.appendFilter(filters.filter, p, s);
            }}
            onSort={handleSort}
            sortState={filters.effectiveSortState}
            initialSort={listData.activeSort}
            initialPage={seededPage}
            initialPageSize={listData.page[1]}
            totalRows={listData.totalRows}
            totalAvailableRows={listData.totalAvailableRows}
            showCheckboxes={canAddToCart}
            actionBar={isProperty ? <TableActionBar onActionClick={onActionClick} isProperty={isProperty}
              isCardView={listData.isCardView} onTableSort={handleSort}
              initialSort={listData.activeSort} selection={listData.selection} /> : undefined}
            renderMobileCard={!isProperty ? (row) => <OrderCard row={row} /> : undefined}
          />
        )}
        {listData.tableData.length === 0 && !listData.isLoading && (
          <EmptyState isProperty={isProperty} isNoList={listData.isNoList} isNoListFilter={listData.isNoListFilter} canCreate={canCreate} />
        )}
      </section>
      {isProperty && (
        <PropertyDetailDrawer
          property={drawerProperty}
          onOpenChange={(open) => !open && setDrawerProperty(null)}
          onAddToCart={listData.handleAddToCart}
          isAddingToCart={!!drawerProperty && listData.addingPropertyId === drawerProperty.id}
          isInCart={drawerProperty ? propertyIdsInCart.has(drawerProperty.id) : false}
        />
      )}
      {listData.showModel && (
        <EditCancelRequestModal headerText={listData.headerText} subHeaderText={listData.subHeaderText}
          orderId={listData.orderId} formattedAddress={listData.formattedAddress}
          placeHolderText={listData.placeHolderText} changeNotes={listData.changeNotes}
          isValid={listData.isValid} isLoader={listData.isLoader} tableAction={listData.tableAction}
          onSubmitEdit={listData.handleEditOrderRequest} onSubmitCancel={listData.handleCancelOrderRequest}
          onCancel={listData.handleModalClose} onChange={handleChangeNotes} inputRef={listData.inputRef} />
      )}
      {!isProperty && (
        <NewOrderDrawer
          open={isNewOrderDrawerOpen}
          onOpenChange={setIsNewOrderDrawerOpen}
        />
      )}
      {isProperty && (
        <NewPropertyDrawer
          open={isNewPropertyDrawerOpen}
          onOpenChange={setIsNewPropertyDrawerOpen}
          // Refetch from page 1 (keeping filters/sort/page size) so the new
          // property is visible under the default updatedAt-desc ordering.
          onCreated={() =>
            filters.appendFilter(
              filters.filter, 1, listData.page[1], listData.sort, filters.moreFilters,
            )
          }
        />
      )}
    </div>
    </PropertyCartProvider>
  );
});

function EmptyState({ isProperty, isNoList, isNoListFilter, canCreate = true }: {
  isProperty: boolean; isNoList: boolean; isNoListFilter: boolean; canCreate?: boolean;
}) {
  if (!isNoList && !isNoListFilter) return null;
  const filterEmpty = isNoListFilter;
  const initialIcon = isProperty ? House : Package;
  return (
    <UIEmptyState
      icon={filterEmpty ? SearchX : initialIcon}
      title={
        filterEmpty
          ? 'No Matches'
          : isProperty
            ? 'No Properties Yet'
            : 'No Orders Yet'
      }
      description={
        filterEmpty
          ? 'Try adjusting your filters.'
          : isProperty
            ? "Properties will appear here once they're added or synced."
            : canCreate
              ? "Orders will appear here once they're placed. Use the New Order button to get started."
              : "Orders will appear here once they're placed."
      }
      className="h-auto border-t border-border py-8"
    />
  );
}
